import React, { useCallback, useMemo, useState } from 'react'
import { Box, Flex } from '@rebass/grid'
import { useTheme } from 'styled-components'
import { FiCheckCircle, FiMinimize2, FiTrash2 } from 'react-icons/fi'
import Text from '../../atoms/Text'
import Image from '../../atoms/Image'
import Button from '../../atoms/Button'
import Modal from '../../molecules/Modal'
import { removeUserImage, setAvatar } from '../../../api/users'
import { handleErrorResponse } from '../../../utlils/general'
import { useAuth } from '../../../contexts/auth'
import { useLoading } from '../../../contexts/loading'

const DISPLAY_NUM = 5

const UserImageGallerySection = () => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewImageId, setPreviewImageId] = useState(null)
  const [confirmRemovalModalOpen, setConfirmRemovalModalOpen] = useState(false)
  const [showAllImages, setShowAllImages] = useState(false)
  const { auth, setAuth, setAuthUser } = useAuth()
  const { setLoading } = useLoading()
  const theme = useTheme()

  const onClickThumbnail = useCallback((userImageId) => {
    setPreviewImageId(userImageId)
    setPreviewModalOpen(true)
  }, [])

  const onSetAvatar = useCallback(() => {
    const body = {
      user_image_id: previewImageId,
    }
    setLoading(true)
    return setAvatar(body)
      .then(({ data: updatedUser }) => {
        setAuthUser((oriUser) => ({
          ...updatedUser,
          user_images: oriUser.user_images || [],
        }))
        setPreviewModalOpen(false)
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [previewImageId, setLoading, setAuth, setAuthUser])

  const onRemoveImage = useCallback(() => {
    setLoading(true)
    setConfirmRemovalModalOpen(false)
    return removeUserImage(previewImageId)
      .then(({ data: updatedUser }) => {
        setAuthUser((oriUser) => ({
          ...updatedUser,
          user_images: (oriUser.user_images || [])
            .filter((ui) => ui.user_image_id !== previewImageId),
        }))
        setPreviewModalOpen(false)
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [previewImageId, setLoading, setAuth, setAuthUser])

  const userImages = useMemo(() => {
    return (auth.user?.user_images || [])
      .sort((ui1, ui2) => ui2.sequence - ui1.sequence)
  }, [auth.user])

  const previewUserImage = useMemo(() => {
    return userImages.find((ui) => ui.user_image_id === previewImageId)
  }, [userImages, previewImageId])

  return (
    <Flex
      width={1}
      alignItems="center"
      justifyContent="center"
      style={{ gap: '0.5rem', flexWrap: 'wrap' }}
    >
      {(showAllImages ? userImages : userImages.slice(0, DISPLAY_NUM)).map((image) => (
        <Image
          key={image.user_image_id}
          src={image.image_url}
          width="min(60px, 14vw)"
          height="min(60px, 14vw)"
          border={image.image_url === auth.user?.avatar_url ? `2px solid ${theme.palette.primary[4]}` : '0.5px solid'}
          onClick={() => onClickThumbnail(image.user_image_id)}
          style={{ cursor: 'pointer' }}
          alt="User image"
          circle
        />
      ))}
      {userImages.length < DISPLAY_NUM && (
        new Array(DISPLAY_NUM - userImages.length).fill(0).map((_, i) => (
          <div
            key={`placeholder-${i}`}
            style={{
              borderRadius: '50%',
              border: '0.5px solid',
              width: 'min(60px, 14vw)',
              height: 'min(60px, 14vw)',
              backgroundColor: theme.palette.grayscale[4],
            }}
          />
        ))
      )}
      {userImages.length > DISPLAY_NUM && (
        <Flex
          alignItems="center"
          justifyContent="center"
          onClick={() => setShowAllImages(!showAllImages)}
          style={{
            cursor: 'pointer',
            borderRadius: '50%',
            border: '1px dashed',
            width: 'min(60px, 14vw)',
            height: 'min(60px, 14vw)',
            borderColor: theme.palette.primary[0],
            backgroundColor: theme.palette.primary[5],
          }}
        >
          {showAllImages
            ? <FiMinimize2 color={theme.palette.primary[0]} size={24} />
            : (
              <Text bold variant="body1" palette="primary" align="center">
                {`+${userImages.length - DISPLAY_NUM}`}
              </Text>
            )}
        </Flex>
      )}
      <Modal
        isOpen={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        showCloseBtn
      >
        <Image
          key={previewImageId}
          src={previewUserImage?.image_url || ''}
          width="100%"
          height="100%"
          borderRadius="0.5rem"
          style={{ maxHeight: '78vh', objectFit: 'contain' }}
        />
        {!!previewUserImage && previewUserImage.image_url !== auth.user?.avatar_url && (
          <Flex width={1} mt="0.5rem">
            <Button padding="0.5rem" onClick={onSetAvatar} style={{ gap: '0.2rem' }}>
              <FiCheckCircle size={16} />
              <Text palette="white">設成頭像</Text>
            </Button>
            <Button
              padding="0.5rem"
              palette="error"
              onClick={() => setConfirmRemovalModalOpen(true)}
              style={{ gap: '0.2rem', marginLeft: '0.5rem' }}
            >
              <FiTrash2 size={16} />
              <Text palette="white">刪除</Text>
            </Button>
          </Flex>
        )}
      </Modal>
      <Modal
        isOpen={confirmRemovalModalOpen}
        onClose={() => setConfirmRemovalModalOpen(false)}
        showCloseBtn
      >
        <Flex flexDirection="column" alignItems="center" p="1rem">
          <Text variant="h3" align="center">確定要刪除此圖片嗎？</Text>
          <Flex mt="1.5rem" width={1}>
            <Box flex={1}>
              <Button
                fullWidth
                variant="outline"
                palette="error"
                onClick={() => setConfirmRemovalModalOpen(false)}
              >
                取消
              </Button>
            </Box>
            <Box flex={1} ml="1rem">
              <Button onClick={onRemoveImage} fullWidth>
                確定
              </Button>
            </Box>
          </Flex>
        </Flex>
      </Modal>
    </Flex>
  )
}

export default UserImageGallerySection
